// src/components/DynamicForm.jsx
import React from 'react';
import { ChevronDown } from 'lucide-react';
import { get } from 'lodash';
import './DynamicForm.css';

const DynamicForm = ({ schema, data, onChange, errors = {}, campaigns = [] }) => {
    const handleFieldChange = (fieldId, value) => {
        onChange(fieldId, value);
    };

    const renderField = (field) => {
        const {
            id,
            type,
            label,
            options,
            placeholder = '',
            required,
            minLength,
            maxLength,
            condition,
        } = field;

        const value = get(data, id, '');
        const error = errors[id];

        // Conditional visibility
        if (condition) {
            const parentValue = get(data, condition.field);
            if (parentValue !== condition.value) return null;
        }

        // Base props for all inputs
        const inputProps = {
            value: value,
            onChange: (e) => {
                const val = e.target.value;
                handleFieldChange(id, val);
            },
            placeholder: placeholder,
            className: `dynamic-input ${error ? 'has-error' : ''}`,
        };

        let inputElement;

        switch (type) {
            case 'select':
                const selectOptions = (id === 'camp_name' && campaigns.length > 0) ? campaigns : options;
                inputElement = (
                    <div className="relative">
                        <select {...inputProps} className="appearance-none">
                            <option value="">Select...</option>
                            {selectOptions?.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                        <ChevronDown
                            size={18}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                        />
                    </div>
                );

                break;

            case 'color':
                inputElement = (
                    <div className="color-input-group">
                        <input
                            type="color"
                            {...inputProps}
                            className="dynamic-color-picker"
                            value={value || '#ffffff'}
                        />
                        <input
                            type="text"
                            {...inputProps}
                            className="dynamic-color-text"
                            placeholder="#ffffff"
                        />
                    </div>
                );
                break;

            case 'textarea':
                inputElement = (
                    <textarea
                        {...inputProps}
                        rows="3"
                        className="dynamic-textarea"
                    />
                );
                break;

            case 'url':
                inputElement = <input type="url" {...inputProps} className="dynamic-input" />;
                break;

            case 'number':
                inputElement = (
                    <input
                        type="number"
                        {...inputProps}
                        className="dynamic-input"
                        min="0"
                        max="360"
                    />
                );
                break;

            default:
                inputElement = <input type="text" {...inputProps} className="dynamic-input" />;
        }

        return (
            <div key={id} className="form-group">
                <label className="dynamic-label">
                    {label} {required && <span className="required-star">*</span>}
                </label>
                {inputElement}
                {minLength && maxLength && (
                    <p className="field-hint">
                        Min: {minLength} characters, Max: {maxLength} characters
                    </p>
                )}
                {error && <p className="field-error">{error}</p>}
            </div>
        );
    };

    // Group fields by section
    const sections = {};
    schema.forEach((field) => {
        const sectionName = field.section || 'General';
        if (!sections[sectionName]) sections[sectionName] = [];
        sections[sectionName].push(field);
    });

    return (
        <div className="dynamic-form">
            {Object.entries(sections).map(([sectionName, fields]) => (
                <div key={sectionName} className="form-section">
                    <h3 className="section-title">{sectionName}</h3>
                    <div className="section-fields">{fields.map(renderField)}</div>
                </div>
            ))}
        </div>
    );
};

export default DynamicForm;